import { useCallback, useRef, useState } from "react";
import { Account } from "@common/types/domains/accounting/account";
import { CreateCoAAccountModal } from "@components/accounting/chartOfAccounts/CoAAccountModal/CreateCoAAccountModal";
import type { CreateAccountRequest } from "@components/accounts/AccountCombobox.types";

type CatalogItemAccountField = "revenueAccountId" | "receivablesAccountId";

interface Params {
    entityId: number | undefined;
    setAccountId: (field: CatalogItemAccountField, accountId: string) => void;
}

// The item form is rendered inside the invoice/template overlay. Opening
// "createCoaAccount" through that overlay replaces the drawer and unmounts
// this form, so account creation stays a local modal.
export function useCatalogItemAccountCreate({
    entityId,
    setAccountId,
}: Params) {
    const targetFieldRef = useRef<CatalogItemAccountField | null>(null);
    const [createAccountRequest, setCreateAccountRequest] =
        useState<CreateAccountRequest | null>(null);

    const openCreateAccount = useCallback(
        (field: CatalogItemAccountField) => (request: CreateAccountRequest) => {
            targetFieldRef.current = field;
            setCreateAccountRequest(request);
        },
        [],
    );

    const onAccountsCreated = useCallback(
        (accounts: Account[]) => {
            const field = targetFieldRef.current;
            if (!field || entityId === undefined) {
                return;
            }
            const match = accounts.find(
                (account) =>
                    "entityId" in account && account.entityId === entityId,
            );
            if (match) {
                setAccountId(field, match.id);
            }
        },
        [entityId, setAccountId],
    );

    const createAccountModal =
        createAccountRequest && entityId !== undefined ? (
            <CreateCoAAccountModal
                show
                onHide={() => setCreateAccountRequest(null)}
                entityIds={[entityId]}
                accountName={createAccountRequest.accountName}
                accountType={createAccountRequest.accountType}
                accountSubtype={createAccountRequest.accountSubtype}
                availableClassifications={
                    createAccountRequest.availableClassifications
                }
                onAccountsCreated={onAccountsCreated}
            />
        ) : null;

    return {
        createAccountModal,
        isCreatingAccount: createAccountRequest !== null,
        openRevenueAccountCreate: openCreateAccount("revenueAccountId"),
        openReceivablesAccountCreate: openCreateAccount("receivablesAccountId"),
    };
}
